import { useCallback, useState } from "react";
import { Alert, Button, PasswordInput, Stack, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconLock } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { portalPasswordService } from "../../services/portalPassword.service";
import { usePortalAuthStore } from "../../store/portalAuth.store";
import {
  PORTAL_PASSWORD_REQUIREMENTS,
  validatePortalPassword,
  validatePortalPasswordConfirmation,
} from "../../utils/portalPasswordValidation";

interface PortalPasswordRegistrationFormProps {
  invitationToken: string;
  caseId: string | null;
}

interface RegistrationFormValues {
  password: string;
  confirmPassword: string;
}

export function PortalPasswordRegistrationForm({
  invitationToken,
  caseId,
}: PortalPasswordRegistrationFormProps) {
  const navigate = useNavigate();
  const login = usePortalAuthStore((state) => state.login);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const validateConfirmedPassword = useCallback(
    (value: string, values: RegistrationFormValues) =>
      validatePortalPasswordConfirmation(value, values.password),
    []
  );
  const form = useForm<RegistrationFormValues>({
    mode: "uncontrolled",
    initialValues: { password: "", confirmPassword: "" },
    validateInputOnBlur: true,
    validate: {
      password: validatePortalPassword,
      confirmPassword: validateConfirmedPassword,
    },
  });

  const handleSubmit = useCallback(
    async (values: RegistrationFormValues) => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        const result = await portalPasswordService.register(
          invitationToken,
          values.password
        );
        login(result.sessionToken, result.email, result.practiceNames);
        navigate(caseId ? `/portal/report/${caseId}` : "/portal/dashboard");
      } catch (error) {
        setSubmitError(
          error instanceof Error
            ? error.message
            : "We couldn't create your password. Please try again."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [caseId, invitationToken, login, navigate]
  );

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Create a password to sign in with your email.
        </Text>
        <PasswordInput
          withAsterisk
          label="Password"
          autoComplete="new-password"
          description={PORTAL_PASSWORD_REQUIREMENTS}
          key={form.key("password")}
          {...form.getInputProps("password")}
        />
        <PasswordInput
          withAsterisk
          label="Confirm password"
          autoComplete="new-password"
          key={form.key("confirmPassword")}
          {...form.getInputProps("confirmPassword")}
        />
        {submitError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
          >
            {submitError}
          </Alert>
        )}
        <Button
          fullWidth
          type="submit"
          loading={isSubmitting}
          leftSection={<IconLock size={18} />}
        >
          Create Password &amp; Continue
        </Button>
      </Stack>
    </form>
  );
}
